import { LoadingOverlay, ScrollArea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getRobot, searchNodeInfoByIds, I18N_LANG_MAP, WIDGET_FOOTER_DEFAULT_TEXT } from '@mygpt/app';
import ContainerHeader from '@/components/Headers/ContainerHeader';
import FormData from '@/components/Widget/Embed/Form';
import Preview from '@/components/Widget/Embed/Preview';
import { useGPTStore } from '@/store';
import { deepMerge } from '@/utils/helper';

import type { I_UserFormValues, NodeItem } from './form-context';
import { WidgetFormProvider } from './form-context';

const DEFAULT_NODE = {
  id: '',
  question: '',
};

// 导出默认的widget配置对象，供其他组件使用
export const DEFAULT_WIDGET_CONFIGS: I_UserFormValues = {
  chatbotIcon: {
    icon: {
      type: 'system',
      url: '',
      index: 0,
    },
    position: {
      checked: false,
      value: 'right',
    },
    customPosition: {
      checked: false,
      x: 16,
      y: 16,
    },
    color: {
      checked: false,
      value: '#6E3DC3',
    },
    stayPage: {
      checked: false,
      value: 1,
    },
    iconButtonText: {
      checked: false,
      value: '',
    },
    iconButtonSize: {
      checked: false,
      value: 0,
    },
  },
  chatbot: {
    multiLanguageTitle: {
      checked: false,
      values: {
        zh: '',
        en: '',
        ja: '',
        ko: '',
      },
    },
    welcomeMessage: {
      checked: false,
      value: 'Hi 👋 What can I help you with?',
      type: 'singleLanguage',
      multiLanguageValues: {
        zh: '你好 👋 有什么我能帮你的吗？',
        en: 'Hi 👋 What can I help you with?',
        ja: 'こんにちは 👋 何かお手伝いできることはありますか？',
        ko: '안녕하세요 👋 무엇을 도와드릴까요?',
      },
    },
    suggestedMessages: {
      checked: false,
      value: '',
      type: '',
      node_list: [],
      node_details: [],
      selectedDatasetId: '',
      selectedNodeIds: [],
      greetingMessage: {
        checked: true,
        value: '',
      },
    },
    signatureMessages: {
      checked: false,
      value: '',
      displayType: 'all',
      list: [],
    },
    chatHeaderDisplay: {
      checked: false,
      value: 'Title',
    },
    inputPlaceholder: {
      checked: false,
      value: 'Ask your question here...',
    },
    securityNotice: {
      checked: true,
    },
    chatGuideText: {
      checked: false,
      value: '',
    },
    footerBrand: {
      checked: true,
      value: WIDGET_FOOTER_DEFAULT_TEXT,
    },
    answerSource: {
      checked: true,
    },
    widgetColor: {
      chatColor: {
        checked: false,
        value: '',
      },
      sendButtonColor: {
        checked: false,
        value: '',
      },
      userMessageColor: {
        checked: false,
        value: '#F1EEFF',
      },
      userMessageTextColor: {
        checked: false,
        value: '#344054',
      },
      botMessageColor: {
        checked: false,
        value: '#F4F7FE',
      },
      botMessageTextColor: {
        checked: false,
        value: '#344054',
      },
    },
    chatHeaderImage: {
      checked: false,
      url: '',
    },
    autoOpenUrls: {
      checked: false,
      urls: [],
    },
    showBotAvatar: {
      checked: false,
    },
  },
  chatbotAvatar: {
    type: 'system',
    url: '',
    index: 0,
  },
  digitalHuman: {
    backgroundColor: {
      checked: true,
      value: '#C5C9EE',
    },
    messageBackgroundOpacity: 50,
    image: 'sparticle',
    textureUrl: undefined,
    playWelcomeMessage: {
      enabled: true,
      mode: 'first',
    },
    defaultInputMode: 'text',
  },
};

export default () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { setRobotInfo: setStoreRobotInfo } = useGPTStore();
  const [viewChange, setViewChange] = useState(0);
  const [mainWrapRight, setMainWrapRight] = useState(0);
  const [widgetConfigs, setWidgetConfigs] = useState<I_UserFormValues>({} as I_UserFormValues);
  const [loading, { close: closeLoading, open: openLoading }] = useDisclosure(false);
  const [selectedNodes, setSelectedNodes] = useState<NodeItem[]>([DEFAULT_NODE]);

  const setSelectedNodesWrap = (data: NodeItem[]) => {
    setSelectedNodes(data);

    const node_list = data.map(({ id }) => id).filter(Boolean);
    const node_details = data
      .map(({ id, dataset_id }) => ({ id, dataset_id: dataset_id || '' }))
      .filter((item) => item.id);

    // 更新旧字段，但保留新字段不被覆盖
    form.setFieldValue('chatbot', {
      ...form.values.chatbot,
      suggestedMessages: {
        ...form.values.chatbot.suggestedMessages,
        node_list,
        node_details,
        // 只在新字段不存在时才设置，避免覆盖transferBox组件的更新
        selectedNodeIds: form.values.chatbot.suggestedMessages.selectedNodeIds || node_list,
      },
    });
  };

  const initSelectedNode = () => {
    setSelectedNodesWrap([DEFAULT_NODE]);
  };
  const addDefaultNode = () => {
    setSelectedNodesWrap([...selectedNodes, DEFAULT_NODE]);
  };
  const updateSelectedNode = (node: NodeItem, index: number) => {
    const data = [...selectedNodes];
    data[index] = node;
    setSelectedNodesWrap(data);
  };
  const deleteSelectedNode = (index: number) => {
    const data = [...selectedNodes];
    data.splice(index, 1);
    setSelectedNodesWrap(data);
  };

  const updateTransferData = (datasetId: string, nodeIds: string[]) => {
    // 保持现有的node_details，只更新当前知识库的主要设置
    const existingNodeDetails = form.values.chatbot.suggestedMessages.node_details || [];

    form.setFieldValue('chatbot', {
      ...form.values.chatbot,
      suggestedMessages: {
        ...form.values.chatbot.suggestedMessages,
        selectedDatasetId: datasetId,
        selectedNodeIds: nodeIds,
        node_list: nodeIds,
        // 保持原有的node_details不变，因为可能包含来自不同知识库的节点
        node_details: existingNodeDetails,
      },
    });
  };

  const mainWrapRef = useRef<HTMLElement>(null);
  const containerHeaderRef = useRef<HTMLDivElement>(null);

  const form = useForm<I_UserFormValues>({
    initialValues: DEFAULT_WIDGET_CONFIGS,
  });

  const onChange = () => {
    setViewChange(viewChange + 1);
  };

  const calcMainDistance = () => {
    if (!mainWrapRef.current) return;
    const { left } = mainWrapRef.current.getBoundingClientRect();
    setMainWrapRight(left - 250);
  };

  const getSelectedNodeInfo = async (nodeList: string[], nodeDetails?: Array<{ id: string; dataset_id: string }>) => {
    if (!id) return;
    const { data } = await searchNodeInfoByIds({
      ai_id: id,
      ids: nodeList,
      language: I18N_LANG_MAP[i18n.language],
    });

    // 优先使用服务器返回的 dataset_id，如果没有则使用保存的 node_details 作为备用
    const nodesWithDatasetId = data.map((node: NodeItem) => {
      const detail = nodeDetails?.find((d) => d.id === node.id);
      return {
        ...node,
        dataset_id: node.dataset_id || detail?.dataset_id || '',
      };
    });

    setSelectedNodes(nodesWithDatasetId);
  };

  const initData = async (id: string) => {
    if (!id) return;
    openLoading();
    const res = await getRobot({ aiId: id });
    setStoreRobotInfo(res.data);
    const widget_configs = res.data?.configs?.widget_configs;
    closeLoading();
    let widgetConfigs = DEFAULT_WIDGET_CONFIGS;
    if (widget_configs) {
      const configs: I_UserFormValues = JSON.parse(widget_configs);
      if (configs.chatbot.suggestedMessages.value && !configs.chatbot.suggestedMessages.type) {
        configs.chatbot.suggestedMessages.type = 'text';
      }
      if (configs.chatbot.suggestedMessages.node_list && configs.chatbot.suggestedMessages.node_list.length) {
        await getSelectedNodeInfo(
          configs.chatbot.suggestedMessages.node_list,
          configs.chatbot.suggestedMessages.node_details
        );

        // 初始化穿梭框相关字段，向后兼容
        if (!configs.chatbot.suggestedMessages.selectedNodeIds) {
          configs.chatbot.suggestedMessages.selectedNodeIds = configs.chatbot.suggestedMessages.node_list || [];
        }
        if (
          !configs.chatbot.suggestedMessages.selectedDatasetId &&
          configs.chatbot.suggestedMessages.node_details &&
          configs.chatbot.suggestedMessages.node_details.length > 0
        ) {
          // 如果有node_details，使用第一个的dataset_id作为selectedDatasetId
          configs.chatbot.suggestedMessages.selectedDatasetId =
            configs.chatbot.suggestedMessages.node_details[0].dataset_id;
        }
      }

      // 当接口返回数据中没有该字段时，设置默认值。
      if (!configs.digitalHuman) configs['digitalHuman'] = DEFAULT_WIDGET_CONFIGS.digitalHuman;

      // 确保textureUrl字段正确处理：只有当真正存在有效值时才保留
      configs.digitalHuman.textureUrl =
        configs.digitalHuman.textureUrl && configs.digitalHuman.textureUrl.trim() !== ''
          ? configs.digitalHuman.textureUrl
          : undefined;

      // 向后兼容：确保新增字段有正确的默认值
      if (!configs.chatbot.suggestedMessages.greetingMessage) {
        configs.chatbot.suggestedMessages.greetingMessage = {
          checked: true,
          value: '',
        };
      }

      widgetConfigs = deepMerge(structuredClone(DEFAULT_WIDGET_CONFIGS), configs);
    }

    form.setValues(widgetConfigs);
    setWidgetConfigs(widgetConfigs);
    onChange();
  };

  useEffect(() => {
    if (!id) return;
    initData(id);
  }, [id]);

  // 监听配置变化，获取已选择的节点信息（语言变化由 SuggestionMessages 组件处理）
  useEffect(() => {
    if (!id || !widgetConfigs.chatbot?.suggestedMessages?.node_list?.length) return;

    // 对于 tree 类型，节点的增删改操作完全由 SuggestionMessages 组件管理
    // 避免重复查询，只有在初始化时或 text 类型时才需要查询
    if (widgetConfigs.chatbot.suggestedMessages.type === 'tree') {
      // tree 类型且已有节点数据时，跳过查询
      if (selectedNodes.length > 0) {
        return;
      }
    }

    getSelectedNodeInfo(
      widgetConfigs.chatbot.suggestedMessages.node_list,
      widgetConfigs.chatbot.suggestedMessages.node_details
    );
  }, [id, widgetConfigs.chatbot?.suggestedMessages?.node_list]);

  useEffect(() => {
    calcMainDistance();
    window.addEventListener('resize', calcMainDistance);

    return () => {
      window.removeEventListener('resize', calcMainDistance);
    };
  }, []);

  return (
    <WidgetFormProvider
      value={{
        form,
        widgetConfigs,
        setWidgetConfigs,
        selectedNodes,
        initSelectedNode,
        setSelectedNodesWrap,
        addDefaultNode,
        updateSelectedNode,
        deleteSelectedNode,
        updateTransferData,
      }}
    >
      <section className="flex gap-10 h-full" ref={mainWrapRef}>
        <section className="flex-auto relative min-w-[500px]">
          <div ref={containerHeaderRef}>
            <ContainerHeader
              title={t('page.widget_embed_title')}
              description={t('page.widget_embed_description')}
              spacing={0}
            />
          </div>
          <div className="h-full overflow-scroll pb-[200px]">
            <form
              onChange={onChange}
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <FormData />
            </form>
          </div>

          <LoadingOverlay visible={loading} overlayProps={{ radius: 'sm', blur: 2 }} />
        </section>

        <section className="flex-none bg-[#F1F4FE] rounded-lg" style={{ right: mainWrapRight }}>
          <Preview viewChange={viewChange} />
        </section>
      </section>
    </WidgetFormProvider>
  );
};
